import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import CrossedLinkIconSrc from '@/assets/icons/link-broken.svg';
import PencilIconSrc from '@/assets/icons/pencil.svg';
import {
  createHyperlink,
  deleteHyperlink,
  getInitiativeById,
  updateHyperlink,
} from '@/features/canvas/api';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { FieldType } from '@/features/canvas/types/field-type';
import { SmallActionButton } from '@/features/ui/components/SmallActionButton/SmallActionButton';
import { components } from '@/types/api';

export interface HyperlinkFormHandle {
  cancelNextCleanup: () => void;
  flushPending: () => Promise<void>;
  markDirty: () => void;
}

type Hyperlink = components['schemas']['Hyperlink'];

type Props = {
  fieldId: number;
  fieldType: FieldType;
  hyperlinks: Hyperlink[];
  setHyperlinks: React.Dispatch<React.SetStateAction<Hyperlink[]>>;
  initiativeFieldType?: components['schemas']['InitiativeField']['type'];
  inputValue?: string;
  readOnly?: boolean;
  selectionRange?: { end: number; start: number } | null;
};

const HyperlinkForm = forwardRef<HyperlinkFormHandle, Props>(
  (
    {
      hyperlinks,
      setHyperlinks,
      fieldId,
      fieldType,
      readOnly,
      initiativeFieldType,
      selectionRange,
      inputValue,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation();

    const queryClient = useQueryClient();
    const { selectedNodeId } = useEditorContext();

    const hyperlinksRef = useRef(hyperlinks);

    const queryClientRef = useRef(queryClient);
    const selectedNodeIdRef = useRef(selectedNodeId);

    useEffect(() => {
      queryClientRef.current = queryClient;
    }, [queryClient]);
    useEffect(() => {
      selectedNodeIdRef.current = selectedNodeId;
    }, [selectedNodeId]);

    useEffect(() => {
      hyperlinksRef.current = hyperlinks;
    }, [hyperlinks]);

    const skipNextCleanup = useRef(false);
    const dirtyDuringSession = useRef(false);

    useImperativeHandle(ref, () => ({
      cancelNextCleanup: () => {
        skipNextCleanup.current = true;
      },
      markDirty: () => {
        dirtyDuringSession.current = true;
      },
      flushPending: async () => {
        await persistAllRows();
        await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);
        dirtyDuringSession.current = false;
        skipNextCleanup.current = false;
      },
    }));

    useEffect(() => {
      skipNextCleanup.current = false;
      dirtyDuringSession.current = false;
    }, []);

    if (readOnly) return null;

    const draftAdded = useRef(false);

    useEffect(() => {
      if (
        !readOnly &&
        !draftAdded.current &&
        selectionRange &&
        selectionRange.start !== selectionRange.end &&
        hyperlinks.length === 0
      ) {
        const draft: Hyperlink = {
          url: '',
          display_text: inputValue?.slice(selectionRange.start, selectionRange.end) ?? '',
          position_start: selectionRange.start,
          position_end: selectionRange.end,
        };

        setHyperlinks(prev => [...prev, draft]);
        dirtyDuringSession.current = true;
        draftAdded.current = true;
      }
    }, [readOnly, selectionRange, hyperlinks, inputValue, setHyperlinks]);

    const updateLink = useCallback(
      (index: number, key: keyof Hyperlink, value: string | number | null) => {
        setHyperlinks((prev: Hyperlink[]) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], [key]: value ?? undefined };
          return updated;
        });
        dirtyDuringSession.current = true;
      },
      [setHyperlinks]
    );

    const persistRow = useCallback(
      async (idx: number) => {
        const link = hyperlinks[idx];
        const requiresSelection = initiativeFieldType !== 'small';
        const hasSelection =
          !!link.display_text && link.position_start != null && link.position_end != null;

        if (requiresSelection && !hasSelection) return;

        if (!link?.url?.trim() && !link?.id) return;

        if (link.url?.trim() === '') {
          if (link.id) await deleteHyperlink(link.id);
          updateLink(idx, 'url', null);
        } else if (link.id) {
          await updateHyperlink(link.id, {
            url: link.url,
            display_text: link.display_text,
            position_start: link.position_start,
            position_end: link.position_end,
          });
        } else {
          const created = await createHyperlink({
            field_type: fieldType,
            field_id: fieldId,
            url: link.url,
            display_text: link.display_text,
            position_start: link.position_start,
            position_end: link.position_end,
          });
          updateLink(idx, 'id', created.id!);
        }

        skipNextCleanup.current = true;
        await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);
        dirtyDuringSession.current = false;
      },
      [hyperlinks, fieldType, fieldId, initiativeFieldType, updateLink, queryClient, selectedNodeId]
    );

    const persistAllRows = useCallback(
      () => Promise.all(hyperlinksRef.current.map((_, i) => persistRow(i))),
      [persistRow]
    );

    const persistAllRowsRef = useRef(persistAllRows);
    useEffect(() => {
      persistAllRowsRef.current = persistAllRows;
    }, [persistAllRows]);

    useEffect(() => {
      return () => {
        if (!skipNextCleanup.current && dirtyDuringSession.current) {
          persistAllRowsRef
            .current()
            .then(() =>
              queryClientRef.current.invalidateQueries([
                getInitiativeById.name,
                selectedNodeIdRef.current,
              ])
            )
            .catch(() => undefined);
        }
        skipNextCleanup.current = false;
        dirtyDuringSession.current = false;
      };
    }, []);

    const deleteLink = async (index: number) => {
      const hyperlink = hyperlinks[index];

      if (!hyperlink.id) return;

      try {
        await deleteHyperlink(hyperlink.id);
        const updated = hyperlinks.filter((_, i) => i !== index);
        setHyperlinks(updated);

        await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);
        dirtyDuringSession.current = false;
      } catch (err) {
        /* empty for now */
      }
    };

    const initialLink: Hyperlink = { url: '' };

    const linksToRender = hyperlinks.length === 0 ? [initialLink] : hyperlinks;

    return (
      <FormWrapper>
        {linksToRender.map((link, idx) => {
          const key = link.id ?? `new-${idx}`;
          const isSaved = !!link.id;
          return (
            <Row key={key}>
              <Input
                type="url"
                placeholder="Enter link here"
                value={link.url}
                onChange={e => updateLink(idx, 'url', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void persistRow(idx);
                  }
                }}
              />

              <SmallActionButton
                onClick={async () => {
                  const requiresSelection = initiativeFieldType !== 'small';

                  if (
                    requiresSelection &&
                    (!link.display_text || link.position_start == null || link.position_end == null)
                  ) {
                    return;
                  }

                  if (link.id) {
                    await updateHyperlink(link.id, {
                      url: link.url,
                      display_text: link.display_text,
                      position_start: link.position_start,
                      position_end: link.position_end,
                    });
                  } else {
                    const createdLink = await createHyperlink({
                      field_type: fieldType,
                      field_id: fieldId,
                      url: link.url,
                      display_text: link.display_text,
                      position_start: link.position_start,
                      position_end: link.position_end,
                    });

                    const updated = [...hyperlinks];
                    updated[idx] = createdLink;
                    setHyperlinks(updated);
                  }

                  skipNextCleanup.current = true;
                  await queryClient.invalidateQueries([getInitiativeById.name, selectedNodeId]);
                  dirtyDuringSession.current = false;
                }}
                text={isSaved ? '' : t('editor.sidebar.initiative.save_button_title')}
                icon={isSaved ? PencilIconSrc : undefined}
              />

              {!!link.id && (
                <DeleteButton
                  type="button"
                  onClick={() => deleteLink(idx)}
                  aria-label={t('common.delete')}
                >
                  <img src={CrossedLinkIconSrc} alt="Delete link" width="14" height="14" />
                </DeleteButton>
              )}
            </Row>
          );
        })}
      </FormWrapper>
    );
  }
);

const FormWrapper = styled.div`
  border-radius: 10px;
`;

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: none;
  border: none;
`;

const DeleteButton = styled.button`
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export { HyperlinkForm };
